import { useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { Modal, useToast } from '@/components/ui';
import { useAuth } from '@/auth/AuthProvider';
import { installActivityTracking, lastActivity, markActive, mediaPlaying, sessionInfo, signoutReason, SESSION_END_TEXT } from '@/lib/session';
import { fmtDateTime } from '@/lib/format';
import { keepAlive } from '@/lib/api';

/** قبل الخمول بدقيقتين يُسأل صاحبُ الجلسة — وقبل الحدّ المطلق بعشر دقائق يُنبَّه مرةً */
const WARN_MS = 2 * 60_000;
const ABSOLUTE_NOTICE_MS = 10 * 60_000;
const TICK_MS = 5_000;

type Phase = { kind: 'ok' } | { kind: 'warn'; remainingMs: number };

/**
 * حارسُ الجلسة (23 سبتمبر 2026): ما تفعله المواقعُ الحكومية لكن بحدٍّ أطول — ستون دقيقةً بلا
 * نقرةٍ ولا كتابة (ثلاثون لإدارة المنصة)، وحدٌّ مطلقٌ ليوم عمل. والخادمُ هو الحَكَم (0054): حتى لو
 * أُغلق هذا التبويب أو عُطّلت هذه الشاشة فلن يُدوَّر رمزٌ بعد الخمول.
 *
 * هنا الإنذارُ وحده: نافذةٌ «هل ما زلت هنا؟» بعدٍّ تنازلي قبل دقيقتين، وزرُّ «متابعة الجلسة». والنشاطُ
 * مشتركٌ بين التبويبات، وفيديو يعمل (بثّ الفصل) نشاط — فلا يُطرد معلّمٌ يشاهد صفّه.
 */
export function SessionGuard() {
  const { status, signOut } = useAuth();
  const toast = useToast();
  const [phase, setPhase] = useState<Phase>({ kind: 'ok' });
  const noticed = useRef<string | null>(null);
  const ending = useRef(false);

  useEffect(() => { installActivityTracking(); }, []);

  useEffect(() => {
    if (status !== 'authenticated') return;
    const tick = () => {
      if (ending.current) return;
      const info = sessionInfo.get();
      const idleLimit = (info?.idleMinutes ?? 60) * 60_000;
      const now = Date.now();
      if (mediaPlaying()) markActive();
      const idle = now - lastActivity();
      const end = (text: string) => {
        ending.current = true;
        signoutReason.set(text);
        setPhase({ kind: 'ok' });
        void signOut().finally(() => { ending.current = false; });
      };
      if (idle >= idleLimit) { end(SESSION_END_TEXT.SESSION_IDLE); return; }
      // نشِطٌ الآن (آخرُ دقيقة) ولم يُكلَّم الخادمُ منذ عشر دقائق؟ نبضٌ يُعلمه
      if (idle < 60_000) void keepAlive();
      if (info) {
        const left = new Date(info.expiresAt).getTime() - now;
        if (left <= 0) { end(SESSION_END_TEXT.SESSION_EXPIRED); return; }
        if (left <= ABSOLUTE_NOTICE_MS && noticed.current !== info.id) {
          noticed.current = info.id;
          toast.push('info', `ستنتهي جلستك عند ${fmtDateTime(info.expiresAt)} (المدّة القصوى لأمان الحساب) — احفظ عملك ثم ادخل من جديد.`);
        }
      }
      setPhase(idle >= idleLimit - WARN_MS ? { kind: 'warn', remainingMs: idleLimit - idle } : { kind: 'ok' });
    };
    tick();
    const t = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(t);
  }, [status, signOut, toast]);

  if (status !== 'authenticated' || phase.kind !== 'warn') return null;
  const secs = Math.max(0, Math.ceil(phase.remainingMs / 1000));
  const mm = String(Math.floor(secs / 60)).padStart(1, '0');
  const ss = String(secs % 60).padStart(2, '0');
  const stay = () => { markActive(true); setPhase({ kind: 'ok' }); };
  return (
    <Modal
      title="هل ما زلت هنا؟"
      description="لم نلحظ نشاطاً منذ مدّة. حمايةً لبيانات الطلبة تُغلق الجلسة تلقائياً إن بقيت بلا نشاط."
      onClose={stay}
      footer={(
        <>
          <button type="button" className="btn btn--primary" onClick={stay}>متابعة الجلسة</button>
          <button type="button" className="btn btn--ghost" onClick={() => { signoutReason.set('خرجت من الجلسة.'); void signOut(); }}>خروج الآن</button>
        </>
      )}
    >
      <p className="session-warn" role="timer" aria-live="polite">
        <Clock size={20} aria-hidden /> تُغلق الجلسة بعد <b><bdi dir="ltr">{mm}:{ss}</bdi></b>
      </p>
    </Modal>
  );
}
